import { X } from 'lucide-react';
import { useEffect } from 'react';
import toast, { type Toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/NewButton';
import {
  APP_INSTALL_EVENT,
  type AppInstallPromptEvent,
  dismissInstallPrompt,
  trackAppInstall,
  useInstallOffer,
  withdrawInstallOffer,
} from '@/lib/appInstall';
import { flushAppInsights } from '@/lib/applicationInsights';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { cn } from '@/lib/utils';

const INSTALL_TOAST_ID = 'app-install';

export const AppInstallToast = ({
  toastInstance,
  installPrompt,
}: {
  toastInstance: Toast;
  installPrompt: AppInstallPromptEvent;
}) => {
  const { t } = useTranslation();

  const dismiss = () => {
    trackAppInstall(APP_INSTALL_EVENT.dismissed);
    flushAppInsights();
    dismissInstallPrompt();
  };

  const install = async () => {
    trackAppInstall(APP_INSTALL_EVENT.clicked);
    try {
      const result = await installPrompt.prompt();
      trackAppInstall(APP_INSTALL_EVENT.outcome, { outcome: result.outcome });
      flushAppInsights();
      withdrawInstallOffer();
    } catch {
      toast.error(t('install-app-failed'));
    }
  };

  return (
    <div
      className={cn(
        'relative flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-[0_3px_10px_rgba(0,0,0,0.1),0_3px_3px_rgba(0,0,0,0.05)] transition-opacity duration-200',
        toastInstance.visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <button
        type='button'
        aria-label={t('close')}
        onClick={dismiss}
        className='absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-700'
      >
        <X className='size-3' />
      </button>
      <span className='whitespace-nowrap text-sm text-gray-900'>{t('install-app-prompt')}</span>
      <Button size='sm' text={t('install-app')} onClick={install} />
    </div>
  );
};

function showInstallToast(installPrompt: AppInstallPromptEvent) {
  toast.custom(
    (toastInstance) => (
      <AppInstallToast toastInstance={toastInstance} installPrompt={installPrompt} />
    ),
    {
      id: INSTALL_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      removeDelay: 250,
    },
  );
}

/**
 * When Chrome or Edge offers to install Crimson, show a dismissible toast to
 * signed-in users. Closing it remembers the choice so we do not ask again.
 */
export function AppInstallPrompt() {
  const { user, isLoading } = useAuth();
  const signedIn = !isLoading && Boolean(user);
  const offer = useInstallOffer();

  useEffect(() => {
    if (!signedIn || !offer) {
      toast.dismiss(INSTALL_TOAST_ID);
      return;
    }
    trackAppInstall(APP_INSTALL_EVENT.offered);
    showInstallToast(offer);
  }, [signedIn, offer]);

  return null;
}
